import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildNetwork,
  cameraTransform,
  CORE_POSITION,
  isDense,
  isExternalUrl,
  nodePosition,
  subNodePositions,
  type NetworkApp,
  type NetworkNode,
  type Point,
} from '../../shared/network';
import { logAuditEvent } from '../lib/audit';
import { logoColor, logoGlowWhite } from '../lib/logos';
import { useReducedMotion } from '../lib/useReducedMotion';
import { ACCENT_HEX, PixelIcon } from './PixelIcon';

const MOBILE_QUERY = '(max-width: 640px)';
/** Wartezeit, bis die Sub-Nodes nach der Kamerafahrt aufpoppen (Mockup: 480ms). */
const SUBNODE_DELAY = 480;

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(MOBILE_QUERY).matches === true,
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const query = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setMobile(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () =>
      setSize({ width: element.clientWidth, height: element.clientHeight });
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, size };
}

interface NodeVisualProps {
  node: NetworkNode;
  position: Point;
  className?: string;
  onActivate?: () => void;
  interactive: boolean;
  hint: string;
}

function NodeVisual({ node, position, className, onActivate, interactive, hint }: NodeVisualProps) {
  const style = { '--x': `${position.x}%`, '--y': `${position.y}%` } as React.CSSProperties;
  const classes = [
    'node',
    `node--${node.kind}`,
    `node--${node.accent}`,
    interactive ? '' : 'node--inert',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <span className="node__discwrap">
        <span className="node__disc">
          {node.kind === 'ghost' ? (
            <span className="node__plus" aria-hidden="true">
              +
            </span>
          ) : (
            <PixelIcon name={node.icon} accent={node.accent} />
          )}
        </span>
        {node.kind !== 'ghost' && <span className="node__cnt">{node.count}</span>}
      </span>
      <span className="node__lbl">{node.label}</span>
      <span className="node__sub">{node.sub}</span>
    </>
  );

  if (!interactive) {
    return (
      <div className={classes} style={style} aria-hidden={node.kind === 'ghost' ? true : undefined}>
        {inner}
      </div>
    );
  }

  return (
    <button type="button" className={classes} style={style} onClick={onActivate} title={hint}>
      {inner}
    </button>
  );
}

export interface NetworkViewProps {
  apps: NetworkApp[];
  isAdmin: boolean;
  onMessage: (message: string) => void;
}

export function NetworkView({ apps, isAdmin, onMessage }: NetworkViewProps) {
  const mobile = useIsMobile();
  const reducedMotion = useReducedMotion();
  const { ref: stageRef, size } = useElementSize<HTMLDivElement>();

  const nodes = useMemo(() => buildNetwork(apps), [apps]);
  const positions = useMemo(
    () => nodes.map((_, index) => nodePosition(index, nodes.length, mobile)),
    [nodes, mobile],
  );
  const core = mobile ? CORE_POSITION.mobile : CORE_POSITION.desktop;

  const [zoomedKey, setZoomedKey] = useState<string | null>(null);
  const [subsVisible, setSubsVisible] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const zoomedIndex = zoomedKey ? nodes.findIndex((n) => n.key === zoomedKey) : -1;
  const zoomedNode = zoomedIndex >= 0 ? nodes[zoomedIndex] : undefined;
  const zoomedPosition = zoomedIndex >= 0 ? positions[zoomedIndex] : undefined;

  const zoomOut = useCallback(() => {
    window.clearTimeout(timer.current);
    setZoomedKey(null);
    setSubsVisible(false);
  }, []);

  useEffect(() => {
    if (!zoomedKey) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') zoomOut();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomedKey, zoomOut]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Beim Wechsel auf einen anderen Breakpoint stimmt die Kamerafahrt nicht mehr.
  useEffect(() => {
    zoomOut();
  }, [mobile, zoomOut]);

  function openApp(url: string, slug: string) {
    void logAuditEvent('app_access', { app: slug }, slug);
    if (isExternalUrl(url)) window.open(url, '_blank', 'noopener,noreferrer');
    else window.location.assign(url);
  }

  function activate(node: NetworkNode) {
    if (node.kind === 'area') {
      window.clearTimeout(timer.current);
      setZoomedKey(node.key);
      // Reduced Motion: harter Wechsel, Sub-Nodes sofort.
      if (reducedMotion) {
        setSubsVisible(true);
      } else {
        setSubsVisible(false);
        timer.current = window.setTimeout(() => setSubsVisible(true), SUBNODE_DELAY);
      }
      return;
    }
    if (node.kind === 'standalone' && node.url) {
      openApp(node.url, node.key.replace('app:', ''));
      return;
    }
    if (node.kind === 'ghost') {
      onMessage('> Bereich anlegen: neuen Eintrag im App-Katalog mit area setzen');
    }
  }

  const worldTransform =
    zoomedPosition && size.width > 0
      ? (() => {
          const t = cameraTransform(zoomedPosition, size);
          return `translate(${t.x}px, ${t.y}px) scale(${t.scale})`;
        })()
      : undefined;

  const subPositions =
    zoomedNode && zoomedPosition
      ? subNodePositions(zoomedPosition, zoomedNode.apps.length, mobile)
      : [];

  return (
    <div
      className={`stage${zoomedKey ? ' stage--zoomed' : ''}${
        isDense(nodes.length, mobile) ? ' stage--dense' : ''
      }`}
      ref={stageRef}
      aria-label="Bereichs-Netzwerk"
    >
      <div
        className={`world${reducedMotion ? ' world--instant' : ''}`}
        style={worldTransform ? { transform: worldTransform } : undefined}
      >
        <svg
          className="wires"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {nodes.map((node, index) => {
            const p = positions[index]!;
            const ghost = node.kind === 'ghost';
            return (
              <g key={node.key} opacity={ghost ? 0.5 : 1}>
                <line
                  className="wires__base"
                  x1={core.x}
                  y1={core.y}
                  x2={p.x}
                  y2={p.y}
                  strokeDasharray={ghost ? '4 8' : undefined}
                />
                <line
                  className="wires__pulse"
                  x1={core.x}
                  y1={core.y}
                  x2={p.x}
                  y2={p.y}
                  style={{ stroke: ACCENT_HEX[node.accent], color: ACCENT_HEX[node.accent] }}
                />
              </g>
            );
          })}

          {zoomedNode &&
            zoomedPosition &&
            subPositions.map((p, i) => (
              <g key={`sub-${i}`}>
                <line
                  className="wires__base"
                  x1={zoomedPosition.x}
                  y1={zoomedPosition.y}
                  x2={p.x}
                  y2={p.y}
                />
                <line
                  className="wires__pulse"
                  x1={zoomedPosition.x}
                  y1={zoomedPosition.y}
                  x2={p.x}
                  y2={p.y}
                  style={{
                    stroke: ACCENT_HEX[zoomedNode.accent],
                    color: ACCENT_HEX[zoomedNode.accent],
                  }}
                />
              </g>
            ))}
        </svg>

        {/* Kern: Logo mit weißer Glow-Unterlage und Glitch (Design-System §4) */}
        <div
          className={`node node--core node--teal node--inert${zoomedKey ? ' node--dim' : ''}`}
          style={{ '--x': `${core.x}%`, '--y': `${core.y}%` } as React.CSSProperties}
        >
          <span className="node__discwrap">
            <span className="node__disc">
              <span className="logo-stack">
                <img className="logo-stack__glow" src={logoGlowWhite} alt="" aria-hidden="true" />
                <img className="logo-stack__mark" src={logoColor} alt="Smiling Data Club" />
              </span>
            </span>
          </span>
          <span className="node__lbl">SMILING DATA HUB</span>
        </div>

        {nodes.map((node, index) => {
          const interactive = node.kind !== 'ghost' || isAdmin;
          const dim = zoomedKey !== null && zoomedKey !== node.key;
          const hint =
            node.kind === 'area'
              ? `Bereich ${node.label} öffnen`
              : node.kind === 'standalone'
                ? `${node.label} öffnen${node.external ? ' (extern)' : ''}`
                : 'Bereich hinzufügen';
          return (
            <NodeVisual
              key={node.key}
              node={node}
              position={positions[index]!}
              className={dim ? 'node--dim' : ''}
              interactive={interactive}
              hint={hint}
              onActivate={() => activate(node)}
            />
          );
        })}

        {zoomedNode?.apps.map((app, i) => {
          const position = subPositions[i];
          if (!position) return null;
          const soon = app.coming_soon === true;
          return (
            <NodeVisual
              key={app.id}
              node={{
                key: `sub:${app.slug}`,
                kind: 'area',
                label: app.name.toUpperCase(),
                sub: soon ? 'coming soon' : '',
                accent: zoomedNode.accent,
                icon: app.icon ?? 'app',
                apps: [],
                count: 0,
              }}
              position={position}
              className={`node--sub${subsVisible ? ' is-shown' : ''}${soon ? ' node--soon' : ''}`}
              // Ohne Ziel nicht anklickbar – ein Klick ins Leere ist schlechter
              // als ein sichtbar wartender Node.
              interactive={!soon}
              hint={soon ? `${app.name} folgt` : `${app.name} öffnen`}
              onActivate={() => openApp(app.url, app.slug)}
            />
          );
        })}
      </div>

      <button type="button" className="back-btn" onClick={zoomOut}>
        ‹ NETZWERK
      </button>
    </div>
  );
}
